import { highchartsTooltipCfg } from '../config/highchartsCfg';

/** Matches ControlsWrapper placement in line-chart widgets (top-right SMA slot). */
export const LINE_CHART_TOOLTIP_TOP_OFFSET = 32;
export const LINE_CHART_TOOLTIP_RIGHT_INSET = 65;
const LINE_CHART_TOOLTIP_MIN_X = 8;
const TOOLTIP_VISIBILITY_BOUND = '__lineChartTooltipVisibilityBound';

type TooltipChart = {
  chartWidth: number;
};

type TooltipPositionerContext = {
  chart: TooltipChart;
};

export const lineChartFixedTooltipPositioner = function (
  this: TooltipPositionerContext,
  labelWidth: number,
  _labelHeight: number,
) {
  const chart = this.chart;
  const x = Math.max(LINE_CHART_TOOLTIP_MIN_X, chart.chartWidth - labelWidth - LINE_CHART_TOOLTIP_RIGHT_INSET);
  const y = LINE_CHART_TOOLTIP_TOP_OFFSET;
  return { x, y };
};

export const lineChartHighchartsTooltipCfg = (tooltipCfg: any) => {
  const baseCfg = highchartsTooltipCfg(tooltipCfg);
  return {
    ...baseCfg,
    followPointer: false,
    stickOnContact: true,
    outside: true,
    positioner: lineChartFixedTooltipPositioner,
    style: {
      ...baseCfg.style,
      zIndex: 20,
    },
  };
};

type MutableTooltip = {
  hide: (...args: any[]) => any;
  refresh: (...args: any[]) => any;
  isHidden?: boolean;
  [TOOLTIP_VISIBILITY_BOUND]?: boolean;
};

/**
 * Highcharts does not reliably emit tooltip "hide"/"refresh" events, so we wrap
 * the methods themselves. Leaving the chart container is a fallback in case hide
 * is delayed or skipped while the pointer is no longer on the plot.
 */
export const bindLineChartTooltipVisibility = (
  chart: { tooltip?: object; container?: HTMLElement } | undefined,
  _HighchartsRef: unknown,
  onVisibilityChange?: (visible: boolean) => void,
): (() => void) | undefined => {
  if (!chart?.tooltip || !onVisibilityChange) {
    return undefined;
  }

  const tooltip = chart.tooltip as MutableTooltip;
  if (tooltip[TOOLTIP_VISIBILITY_BOUND]) {
    return undefined;
  }

  const originalHide = tooltip.hide.bind(tooltip);
  const originalRefresh = tooltip.refresh.bind(tooltip);

  tooltip.refresh = (...args: any[]) => {
    onVisibilityChange(true);
    return originalRefresh(...args);
  };

  tooltip.hide = (...args: any[]) => {
    const result = originalHide(...args);
    onVisibilityChange(false);
    return result;
  };

  tooltip[TOOLTIP_VISIBILITY_BOUND] = true;

  const handlePointerLeave = () => {
    // Defer so Highcharts can finish its own hide path first; still restore SMA
    // if hide never ran (common with outside/stickOnContact tooltips).
    window.setTimeout(() => {
      if (tooltip.isHidden !== false) {
        onVisibilityChange(false);
      }
    }, 50);
  };

  chart.container?.addEventListener('mouseleave', handlePointerLeave);

  return () => {
    tooltip.hide = originalHide;
    tooltip.refresh = originalRefresh;
    delete tooltip[TOOLTIP_VISIBILITY_BOUND];
    chart.container?.removeEventListener('mouseleave', handlePointerLeave);
    onVisibilityChange(false);
  };
};
